import type { MutableRefObject } from 'react'
import React from 'react'
import ReactTooltip from 'react-tooltip'
import { secondsToFullString, secondsToString, useBoolean } from '@mote/helpers'
import { isMobile } from '@mote/helpers/browser'
import { Tooltip } from '../TooltipV2'
import { VisuallyHidden } from '../visuallyHidden'
import {
  StyledSlider,
  StyledSliderThumb,
  StyledSliderTrack,
  StyledSliderTrackFilled,
  getSliderAnimationValues
} from './Scrubber.styles'

type TooltipDynamicProps = {
  target: {
    ref?: MutableRefObject<HTMLDivElement | null>
    'data-for'?: string
    'data-tip'?: string
  }
  tooltip: {
    effect?: ReactTooltip['props']['effect']
    overridePosition?: ReactTooltip['props']['overridePosition']
  }
}

export interface ScrubberProps {
  animated?: boolean
  showTooltip?: boolean
  currentTime: number
  duration: number
  onSeek: (value: number) => void
  onSeekEnd: (value: number) => void
}

/*
 * TODO: Fix tooltip positioning on drag
 * */
export const Scrubber = (props: ScrubberProps): JSX.Element => {
  const sliderRef = React.useRef<HTMLDivElement>(null)
  const tooltipRef = React.useRef<HTMLDivElement | null>(null)
  const [isActive, activeActions] = useBoolean(false)
  const [isHovering, hoveringActions] = useBoolean(false)
  const styles = getSliderAnimationValues(
    props.animated
      ? !isMobile() && (isHovering || isActive)
        ? 'final'
        : 'initial'
      : 'final'
  )

  function getTooltipDynamicProps(): TooltipDynamicProps {
    if (!props.showTooltip || isMobile()) return { target: {}, tooltip: {} }

    return {
      target: {
        ref: tooltipRef,
        'data-for': 'mote-player-scrubber-tooltip',
        'data-tip': secondsToString(props.currentTime)
      },
      tooltip: {
        effect: 'float',
        overridePosition: handleOnSliderMouseMove
      }
    }
  }

  const toggleTooltip = (state: 'show' | 'hide') => {
    if (!tooltipRef.current) return

    ReactTooltip[state](tooltipRef.current)
  }

  const handleOnSliderMouseMove: ReactTooltip['props']['overridePosition'] = (
    position,
    currentEvent,
    currentTarget,
    node,
    place,
    desiredPlace,
    effect,
    offset
  ) => {
    if (!sliderRef.current || !node) return { top: 0, left: 0 }

    const sliderRect = sliderRef.current.getBoundingClientRect()

    let percent = (100 / sliderRect.width) * (position.left - sliderRect.left)
    let left = position.left

    if (left < sliderRect.left) {
      left = sliderRect.left
    } else if (left + node.clientWidth > sliderRect.right) {
      left = sliderRect.right - node.clientWidth
    }

    if (percent < 0) {
      percent = 0
    } else if (percent > 100) {
      percent = 100
    }

    const { childNodes } = node

    if (childNodes.length > 0) {
      const textNode = [...childNodes].find(child => child.nodeType === 3)
      if (textNode) {
        textNode.textContent = secondsToString(
          Math.ceil((props.duration / 100) * percent)
        )
      }
    }

    const normalisedOffset = offset.top || 0

    return {
      top: sliderRect.top - node.clientHeight - normalisedOffset / 2,
      left
    }
  }

  const handleValueChangeEnd = (value: number) => {
    props.onSeekEnd(value)
    if (isActive && !isMobile()) activeActions.setFalse()
  }

  const handleValueChangeStart = () => {
    if (isMobile()) return
    if (isHovering) activeActions.setTrue()
  }

  const handleOnSliderMouseEnter = () => {
    if (isMobile()) return
    hoveringActions.setTrue()

    toggleTooltip('show')
  }

  const handleOnSliderMouseLeave = () => {
    if (isMobile()) return
    hoveringActions.setFalse()

    toggleTooltip('hide')
  }

  return (
    <StyledSlider
      id='mote-player-scrubber'
      data-testid='mote-player-scrubber'
      aria-label='Audio timeline'
      value={props.currentTime.toFixed(2)}
      min={0}
      max={props.duration}
      getAriaValueText={(value: number) => secondsToFullString(value)}
      onChangeStart={handleValueChangeStart}
      onChange={props.onSeek}
      onChangeEnd={handleValueChangeEnd}
      onMouseEnter={handleOnSliderMouseEnter}
      onMouseLeave={handleOnSliderMouseLeave}
      onMouseMove={handleOnSliderMouseMove}
      focusThumbOnChange={false}
      animated={props.animated}
      {...getTooltipDynamicProps().target}
      {...styles.root}
    >
      {props.showTooltip && !isMobile() && (
        <Tooltip
          id='mote-player-scrubber-tooltip'
          place='top'
          offset={16}
          {...getTooltipDynamicProps().tooltip}
        />
      )}
      <StyledSliderTrack ref={sliderRef} animated={props.animated}>
        <StyledSliderTrackFilled animated={props.animated} />
      </StyledSliderTrack>
      <StyledSliderThumb animated={props.animated} {...styles.thumb} />
      <VisuallyHidden>{`Played: ${secondsToFullString(
        props.currentTime
      )} of total duration (${secondsToFullString(
        props.duration
      )})`}</VisuallyHidden>
    </StyledSlider>
  )
}
